import { cva } from 'class-variance-authority'
import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/cn'

/**
 * The desktop Grid (193:5282): the Heading across the top, the Side menu down the
 * left and the content on Background. The sizes are the organisms' own, as on the
 * full screens (321:25442): a 64px Heading and a 220px (or 72.25px collapsed)
 * menu. The Grid board itself sketches a 70px header and a 200px sidebar; the
 * screens and components are what this follows.
 *
 * `atlas-root` sets the kit's typeface, Body style and Dark ink for plain text in
 * the content, which the app's legacy body styles would otherwise set (ADR-0044).
 */
export const appShellVariants = cva('atlas-root flex min-h-screen w-full flex-col bg-atlas-background font-atlas-sans')

export interface AppShellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	/** The top bar, normally a Heading. */
	heading: ReactNode
	/** The left column, normally a SideMenu; it stretches to the content's height. */
	sideMenu: ReactNode
	/** The screen, rendered in the `main` landmark. */
	children: ReactNode
}

/** The page frame every migrated screen sits in: three slots, no behaviour of its own. */
export function AppShell({ heading, sideMenu, children, className, ...props }: AppShellProps) {
	return (
		<div {...props} className={cn(appShellVariants(), className)}>
			<div className="w-full shrink-0">{heading}</div>
			<div className="flex min-h-0 w-full flex-1">
				<div className="flex shrink-0">{sideMenu}</div>
				<main className="min-w-0 flex-1 bg-atlas-background">{children}</main>
			</div>
		</div>
	)
}
